import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Button, Grid } from "@mui/material";
import { Autocomplete, DateTime, Dialog, TextField, Typography } from "@totalsoft/rocket-ui";
import { getErrors, isValid } from "@totalsoft/pure-validations-react";

export const EmployeeModalComponent = ({
  showModal,
  saving,
  hasWritePermission,
  isDirty,
  employee,
  employees,
  validation,
  loadPersons,
  loadPartnerStates,
  loadDepartments,
  onCloseEmployeeModal,
  onPropertyChange,
  onSaveEmployee
}) => {
  const { t } = useTranslation();
  const disabled = useMemo(() => saving || !hasWritePermission, [saving, hasWritePermission]);

  return (
    <Dialog
      id={"employee-component-dialog"}
      aria-labelledby="employee-dialog-title"
      open={showModal}
      showX={false}
      maxWidth="lg"
      actions={
        <Box display="flex" gap={1} mb={1} mx={1}>
          <Button
            variant={"contained"}
            disabled={disabled || !isValid(validation) || !isDirty}
            onClick={() => onSaveEmployee(employee)}
          >
            {saving ? t("Saving") : t("Save")}
          </Button>
          <Button onClick={() => onCloseEmployeeModal()} variant={"contained"} disabled={saving}>
            {t("Close")}
          </Button>
        </Box>
      }
      content={
        employee && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <div>
                <Typography variant="h6">{t("Employee.Title")}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      label={t("Employee.Name")}
                      value={{ id: employee.employeeId, partnerName: employee.employeeName }}
                      onChange={event => {
                        onPropertyChange({ employeeId: event.id, employeeName: event.partnerName });
                      }}
                      isClearable={false}
                      valueKey="id"
                      labelKey="partnerName"
                      loadOptions={event => loadPersons(event)}
                      disabled={disabled || employee.employeeId !== 0}
                      error={!isValid(validation.employeeName)}
                      helperText={getErrors(validation.employeeName)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DateTime
                      id={employee.employeeId + "_hireDate"}
                      fullWidth
                      label={t("Employee.HireDate")}
                      value={employee.hireDate || ""}
                      onChange={event => {
                        onPropertyChange({ hireDate: event });
                      }}
                      disabled={disabled}
                      error={!isValid(validation.hireDate)}
                      helperText={getErrors(validation.hireDate)}
                      disableFuture={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_badge"}
                      fullWidth
                      label={t("Employee.Badge")}
                      value={employee.badge || ""}
                      onChange={event => {
                        onPropertyChange({ badge: event });
                      }}
                      disabled={disabled}
                      error={!isValid(validation.badge)}
                      helperText={getErrors(validation.badge)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      label={t("Employee.ReportsTo")}
                      id={employee.employeeId + "_reportsTo"}
                      value={{ employeeId: employee.reportsToId, employeeName: employee.reportsToName }}
                      onChange={event => {
                        onPropertyChange({ reportsToId: event.employeeId, reportsToName: event.employeeName });
                      }}
                      valueKey="employeeId"
                      labelKey="employeeName"
                      options={employees}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_address"}
                      fullWidth
                      label={t("Employee.Address")}
                      onChange={event => {
                        onPropertyChange({ address: event });
                      }}
                      value={employee.address || ""}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      id={employee.employeeId + "_department"}
                      label={t("Employee.Department")}
                      value={{ departmentId: employee.departmentId, departmentName: employee.departmentName }}
                      onChange={event => {
                        onPropertyChange(event);
                      }}
                      valueKey="departmentId"
                      labelKey="departmentName"
                      loadOptions={() => loadDepartments()}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      id={employee.employeeId + "_employeeState"}
                      label={t("Employee.State")}
                      value={{ id: employee.employeeStateId, name: employee.employeeStateName }}
                      onChange={event => {
                        onPropertyChange({ employeeStateId: event.id, employeeStateName: event.name });
                      }}
                      valueKey="id"
                      labelKey="name"
                      loadOptions={() => loadPartnerStates()}
                      disabled={disabled}
                      error={!isValid(validation.employeeStateId)}
                      helperText={getErrors(validation.employeeStateId)}
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <div>
                <Typography variant="h6">{t("Employee.Person.Title")}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_firstName"}
                      fullWidth
                      label={t("Employee.Person.FirstName")}
                      value={employee.person?.firstName || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_lastName"}
                      fullWidth
                      label={t("Employee.Person.LastName")}
                      value={employee.person?.lastName || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_gender"}
                      fullWidth
                      label={t("Employee.Person.Gender")}
                      value={employee.person?.genderCode || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DateTime
                      id={employee.employeeId + "_birthDate"}
                      fullWidth
                      label={t("Employee.Person.BirthDate")}
                      value={employee.person?.birthDate || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_maritalStatus"}
                      fullWidth
                      label={t("Employee.Person.MaritalStatus")}
                      value={employee.person?.maritalStatus || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_idSeriesAndNumber"}
                      fullWidth
                      label={t("Employee.Person.IdSeriesAndNumber")}
                      value={employee.person?.idSeriesAndNumber || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_idIssuedBy"}
                      fullWidth
                      label={t("Employee.Person.IdIssuedBy")}
                      value={employee.person?.idIssuedBy || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_citizenshipName"}
                      fullWidth
                      label={t("Employee.Person.CitizenshipName")}
                      value={employee.person?.citizenshipName || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_email"}
                      fullWidth
                      label={t("Employee.Person.Email")}
                      value={employee.person?.email || ""}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={employee.employeeId + "_positionName"}
                      fullWidth
                      label={t("Employee.Person.PositionName")}
                      value={employee.person?.positionName || ""}
                      disabled={true}
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        )
      }
    />
  );
};

EmployeeModalComponent.propTypes = {
  showModal: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  hasWritePermission: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  employee: PropTypes.object.isRequired,
  employees: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
  loadPersons: PropTypes.func,
  loadPartnerStates: PropTypes.func,
  loadDepartments: PropTypes.func,
  onCloseEmployeeModal: PropTypes.func,
  onPropertyChange: PropTypes.func,
  onSaveEmployee: PropTypes.func
};

export default EmployeeModalComponent;
